import { createSlice } from "@reduxjs/toolkit"

export const specialistsSlice = createSlice({
  name: "specialists",
  initialState: {
    diver: {
      title: "Diver",
      subtitle: "Spawning fish",
      role: "diver",
      uploadType: 1,
      description: "Divers play a key role in monitoring underwater ecosystems. As a diver specialist you can submit first-hand observations from your dives, helping researchers track biodiversity, water quality, and environmental changes over time.",
      dataCollected: [
        { key: "location", label: "Location", required: true },
        { key: "datetime", label: "Datetime", required: true },
        { key: "depth", label: "Spawning depth", required: true },
        { key: "current", label: "Current", required: false },
        { key: "species", label: "Species", required: false },
        { key: "number_of_individuals", label: "Number of individuals", required: false },
        {
          key: "attachments",
          label: "Attachments",
          required: true,
          requiredWhen: "outsideBonaireBbox",
          accept: {
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "image/webp": [".webp"],
            "image/gif": [".gif"],
            "image/svg+xml": [".svg"],

            "video/mp4": [".mp4"],
            "video/webm": [".webm"],
            "video/quicktime": [".mov"],
          }
        },
        { key: "additional_information", label: "Additional information", required: false }
      ],
      analyse: false,
      upload: true,
      partners: [
        { name: "Dive Friends Bonaire", image: "dive_friends_bonaire.png", url: "https://www.divefriendsbonaire.com" },
        { name: "WWF", image: "wwf.png", url: "https://www.worldwildlife.org" }
      ]
    },
    fisher: {
      title: "Fisher",
      subtitle: "Fish roe",
      role: "fisher",
      uploadType: 2,
      description: "Fisher specialist description goes here.",
      dataCollected: [
        { key: "location", label: "Location", required: true },
        { key: "datetime", label: "Datetime", required: true },
        {
          key: "attachments",
          label: "Attachments",
          required: true,
          accept: {
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "image/webp": [".webp"],
            "image/gif": [".gif"],
            "image/svg+xml": [".svg"],
          }
        },
        { key: "additional_information", label: "Additional information", required: false }
      ],
      analyse: false,
      upload: true,
      partners: [
        { name: "Piskabon", image: "piskabon.png", url: "https://www.facebook.com/piskabon" },
        { name: "Stinapa", image: "stinapa.png", url: "https://stinapabonaire.org" },
        { name: "WWF", image: "wwf.png", url: "https://www.worldwildlife.org" }
      ]
    },
    aquarist: {
      title: "Aquarist",
      subtitle: "Fish egg / larvae",
      role: "aquarist",
      uploadType: 3,
      description: "Aquaria scientist specialist description goes here.",
      dataCollected: [
        { key: "tank_id", label: "Tank", required: true },
        { key: "datetime", label: "Datetime", required: true },
        {
          key: "attachments",
          label: "Attachments",
          required: true,
          accept: {
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "image/webp": [".webp"],
            "image/gif": [".gif"],
            "image/svg+xml": [".svg"],

            "video/mp4": [".mp4"],
            "video/webm": [".webm"],
            "video/quicktime": [".mov"],
          }
        },
        // `informational` velden vragen niets; ze leggen uit wat er ná het uploaden nog kan. Ze
        // horen daarom niet in de "verzamelde gegevens"-chips bovenaan het formulier. Deze staat
        // direct onder de bijlagen, want daar gaat hij over: dezelfde vondst, later opnieuw op de foto.
        { key: "egg_trail_info", label: "Egg trail", required: false, informational: true },
        { key: "attachment_type", label: "Attachments type", required: true },
        // Hoe oud het ei of de larve zelf is: een schatting in dagen en uren, los van het moment
        // waarop de foto is gemaakt. De ΔT van een vervolgmoment is een ander getal en staat in de
        // "Add moment"-dialoog.
        { key: "egg_larvae_age", label: "Egg / larvae age", required: false }
      ],
      analyse: true,
      upload: true,
      addOccurrence: true,
      partners: [
        { name: "Blijdorp", image: "blijdorp.png", url: "https://diergaardeblijdorp.nl/en" },
        { name: "Eaza", image: "eaza.png", url: "https://www.eaza.net" },
        { name: "WWF", image: "wwf.png", url: "https://www.worldwildlife.org" },
        { name: "EUAC", image: "euac.png", url: "https://www.euac.org" }
      ]
    },
    scientist: {
      title: "Researcher",
      subtitle: "Fish egg / larvae",
      role: "scientist",
      uploadType: 4,
      description: "Wild scientist specialist description goes here.",
      dataCollected: [
        { key: "location", label: "Location", required: true },
        { key: "datetime", label: "Datetime", required: true },
        {
          key: "attachments",
          label: "Attachments",
          required: true,
          accept: {
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "image/webp": [".webp"],
            "image/gif": [".gif"],
            "image/svg+xml": [".svg"],

            "video/mp4": [".mp4"],
            "video/webm": [".webm"],
            "video/quicktime": [".mov"],
          }
        },
        { key: "egg_trail_info", label: "Egg trail", required: false, informational: true },
        { key: "attachment_type", label: "Attachments type", required: true },
        { key: "spawning_method", label: "Spawning method", required: false },
        // Het paaien zelf gezien: dan is het moment daarvan bekend, en dat ligt vóór de foto. De
        // datumtijd bovenin het formulier is die van de foto; dit veld zegt hoeveel eerder het
        // paaien was, en daarmee ook hoe oud het ei op de foto is.
        { key: "spawning_datetime", label: "Spawning_datetime_label", required: false, showWhen: "newSpawningObservation", group: "spawning_method" },
        // Alleen te schatten als je het paaien niet hebt gezien; had je het wel gezien, dan is de
        // leeftijd nul en zet het formulier hem zelf.
        //
        // `group` zegt onder welk veld deze vier horen: het formulier tekent ze binnen de kaart van
        // dat veld in plaats van als losse secties eronder.
        { key: "egg_larvae_age", label: "Egg / larvae age", required: false, showWhen: "spawningNotObserved", group: "spawning_method" },
        // Zichtbaar én verplicht onder dezelfde voorwaarde: wie zegt het paaien zelf te hebben
        // gezien, was erbij, en weet dus op welke diepte en bij welke stroming. Bij de duiker is
        // diepte om dezelfde reden verplicht.
        { key: "depth", label: "Spawning depth", required: false, showWhen: "newSpawningObservation", requiredWhen: "newSpawningObservation", group: "spawning_method" },
        { key: "current", label: "Current", required: false, showWhen: "newSpawningObservation", requiredWhen: "newSpawningObservation", group: "spawning_method" },
        { key: "number_of_individuals", label: "Group size", required: false, showWhen: "newSpawningObservation", group: "spawning_method" },
        { key: "species", label: "Species", required: false },
        { key: "dna_identified", label: "DNA identification", required: false },
        // Dezelfde vijf velden als de "Add DNA result"-dialoog. Alleen bij een nieuwe waarneming:
        // bij een bestaande komt de lijst met resultaten (`dna_results`) in de plaats, waar een
        // admin elk resultaat apart corrigeert.
        { key: "dna_barcode", label: "Dna_target_gene_label", required: false, showWhen: "dnaIdentifiedOnNew", group: "dna_identified" },
        { key: "dna_percent_identity", label: "Dna_percent_identity_label", required: false, showWhen: "dnaIdentifiedOnNew", group: "dna_identified" },
        { key: "dna_sequence", label: "Dna_sequence_label", required: false, showWhen: "dnaIdentifiedOnNew", group: "dna_identified" },
        { key: "dna_lab", label: "Dna_lab_label", required: false, showWhen: "dnaIdentifiedOnNew", group: "dna_identified" },
        { key: "dna_sequenced_at", label: "Dna_sequenced_at_label", required: false, showWhen: "dnaIdentifiedOnNew", group: "dna_identified" },
        { key: "dna_later_info", label: "DNA later", required: false, showWhen: "dnaNotIdentifiedOnNew", informational: true, group: "dna_identified" },
        { key: "dna_results", label: "Dna_results_title", required: false, showWhen: "dnaResultsPresent", informational: true }
      ],
      analyse: true,
      upload: true,
      addOccurrence: true,
      partners: [
        { name: "Blijdorp", image: "blijdorp.png", url: "https://diergaardeblijdorp.nl/en" },
        { name: "WWF", image: "wwf.png", url: "https://www.worldwildlife.org" }
      ]
    },

    "aquarist-admin": {
      title: "Tank",
      subtitle: "Add new tank",
      role: "aquarist-admin",
      uploadType: 5,
      description: "Description goes here.",
      dataCollected: [
        { key: "organization_name", label: "Organization name", required: true },
        { key: "tank_name", label: "Tank name", required: true },
        { key: "water_parameters", label: "Water parameters", required: true },
        { key: "moonlight_parameters", label: "Moonlight parameters", required: false },
        { key: "species", label: "Species", required: true },
        { key: "season_information", label: "Season information", required: false },
        { key: "additional_information", label: "Additional information", required: false }
      ],
      analyze: false,
      upload: false
    }
  },
  reducers: {
    set: (state, action) => action.payload
  }
})

/**
 * De realm-rollen die deze applicatie kent, en de enige die het beheerscherm toont of uitdeelt.
 *
 * De vijf specialistrollen komen uit de definities hierboven, zodat een nieuwe specialist er
 * automatisch bij komt. `admin` en `analyzer` staan er los bij: dat zijn geen specialisten maar
 * rechten binnen de app, en ze hebben dus geen `dataCollected`.
 *
 * Een realm bevat meer dan dit: `default-roles-…`, `offline_access` en wat er verder in Keycloak
 * leeft. Die zeggen niets over wat iemand in Eggspedition mag, dus horen ze hier niet in beeld.
 */
export const eggspeditionRoles = (specialists) => [
  ...Object.keys(specialists || {}),
  "admin",
  "analyzer"
]

export const { set: setSpecialists } = specialistsSlice.actions

export default specialistsSlice.reducer
